import { Fragment, useMemo, useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import { cn } from "@/lib/utils";
import { formatHours } from "@/lib/format";
import { useBudgetLinesForProjects } from "@/hooks/use-monday-import";
import type { PreviewItem, ImportOverride } from "@/hooks/use-monday-import";
import { usePermissions } from "@/hooks/use-permissions";
import { CreateInternalProjectInlineForm } from "@/components/admin/CreateInternalProjectInlineForm";

const PHASE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "pl", label: "Prosjektledelse" },
  { value: "preprod", label: "Preproduksjon" },
  { value: "production", label: "Produksjon" },
  { value: "postprod", label: "Postproduksjon" },
  { value: "stills", label: "Stills" },
  { value: "reise", label: "Reise" },
];

const NONE_VALUE = "__none__";

const MATCH_BADGE: Record<string, string> = {
  exact: "bg-green-100 text-green-700",
  phase: "bg-yellow-100 text-yellow-700",
  project_only: "bg-orange-100 text-orange-700",
  skipped: "bg-gray-100 text-gray-600",
};

interface Props {
  preview: PreviewItem[];
  overrides: Record<string, ImportOverride>;
  onOverrideChange: (itemKey: string, patch: ImportOverride) => void;
}

/**
 * Tabell som lar admin justere fase + budget_line per item før bekreftelse.
 * Exact-matches kan kollapses; ikke-exact (phase / project_only) er
 * synlig som default så man raskt kan rydde dem.
 *
 * Override sendes med ved bekreft-import slik at edge function skriver
 * eksplisitt valg i stedet for å regne fuzzy-match på nytt.
 */
export function ImportPreviewTable({ preview, overrides, onOverrideChange }: Props) {
  const [showExact, setShowExact] = useState(false);
  const { isAdmin } = usePermissions();
  // VIB-724a: itemKey (monday_item_id som string) for raden som har inline
  // "opprett internprosjekt"-form åpen. Bare én rad om gangen.
  const [creatingFor, setCreatingFor] = useState<string | null>(null);

  // Hent budget_lines for alle prosjekter som dukker opp i preview.
  // Inkluder også project_id som er satt via inline-opprett-override slik
  // at budget_lines lastes for ferske internprosjekter umiddelbart.
  const projectIds = useMemo(() => {
    const set = new Set<string>();
    for (const p of preview) {
      const overridden = overrides[String(p.monday_item_id)]?.project_id;
      const effective = overridden ?? p.project_id;
      if (effective) set.add(effective);
    }
    return Array.from(set);
  }, [preview, overrides]);
  const { data: budgetLines = [] } = useBudgetLinesForProjects(projectIds);

  // Indekser budget_lines per prosjekt → list, og per prosjekt+phase → list.
  const budgetLinesByProject = useMemo(() => {
    const m = new Map<string, typeof budgetLines>();
    for (const bl of budgetLines) {
      const arr = m.get(bl.project_id) ?? [];
      arr.push(bl);
      m.set(bl.project_id, arr);
    }
    return m;
  }, [budgetLines]);

  const grouped = useMemo(() => {
    const exact: PreviewItem[] = [];
    const editable: PreviewItem[] = [];
    const skipped: PreviewItem[] = [];
    for (const p of preview) {
      if (p.match_level === "skipped") skipped.push(p);
      else if (p.match_level === "exact") exact.push(p);
      else editable.push(p);
    }
    return { exact, editable, skipped };
  }, [preview]);

  return (
    <div className="space-y-4">
      {grouped.editable.length > 0 && (
        <div>
          <h4 className="text-sm font-medium mb-2">
            Trenger justering ({grouped.editable.length})
          </h4>
          <ItemTable
            items={grouped.editable}
            overrides={overrides}
            onOverrideChange={onOverrideChange}
            budgetLinesByProject={budgetLinesByProject}
            isAdmin={isAdmin}
            creatingFor={creatingFor}
            onStartCreate={setCreatingFor}
            onCancelCreate={() => setCreatingFor(null)}
          />
        </div>
      )}

      {grouped.exact.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setShowExact((v) => !v)}
            className="text-sm font-medium text-green-700 hover:underline flex items-center gap-1"
          >
            <SrIcon
              name={showExact ? "chevron-down" : "chevron-right"}
              className="w-3.5 h-3.5"
            />
            {grouped.exact.length} perfekt matchet — {showExact ? "skjul" : "vis"}
          </button>
          {showExact && (
            <div className="mt-2">
              <ItemTable
                items={grouped.exact}
                overrides={overrides}
                onOverrideChange={onOverrideChange}
                budgetLinesByProject={budgetLinesByProject}
                isAdmin={isAdmin}
                creatingFor={creatingFor}
                onStartCreate={setCreatingFor}
                onCancelCreate={() => setCreatingFor(null)}
              />
            </div>
          )}
        </div>
      )}

      {grouped.skipped.length > 0 && (
        <SkippedItems
          items={grouped.skipped}
          overrides={overrides}
          onOverrideChange={onOverrideChange}
          isAdmin={isAdmin}
          creatingFor={creatingFor}
          onStartCreate={setCreatingFor}
          onCancelCreate={() => setCreatingFor(null)}
        />
      )}
    </div>
  );
}

interface SkippedItemsProps {
  items: PreviewItem[];
  overrides: Record<string, ImportOverride>;
  onOverrideChange: (itemKey: string, patch: ImportOverride) => void;
  isAdmin: boolean;
  creatingFor: string | null;
  onStartCreate: (itemKey: string) => void;
  onCancelCreate: () => void;
}

/**
 * VIB-724a: Skipped-rader uten matchende project_code er primær-målet for
 * inline-opprett-internprosjekt. Vi viser hele listen (ikke kun 10) når admin
 * har en åpen create-form slik at admin kan jobbe seg gjennom dem uten å
 * miste oversikten.
 */
function SkippedItems({
  items,
  overrides,
  onOverrideChange,
  isAdmin,
  creatingFor,
  onStartCreate,
  onCancelCreate,
}: SkippedItemsProps) {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? items : items.slice(0, 10);

  return (
    <div>
      <h4 className="text-sm font-medium mb-2 text-muted-foreground">
        Hopper over ({items.length}) — mangler project_code
      </h4>
      <ul className="text-xs text-muted-foreground space-y-1 pl-4 list-disc">
        {visible.map((p) => {
          const key = String(p.monday_item_id);
          const override = overrides[key] ?? {};
          const overrideProjectId = override.project_id ?? null;
          const isCreating = creatingFor === key;
          return (
            <li key={p.monday_item_id} className="space-y-1">
              <div>
                <span>{p.name}</span>{" "}
                <span className="italic">— {p.skip_reason ?? "ukjent grunn"}</span>
                {overrideProjectId && (
                  <span className="ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700">
                    Knyttet til nytt internprosjekt
                  </span>
                )}
                {isAdmin && !overrideProjectId && !isCreating && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="ml-2 h-6 text-[10px] px-2"
                    onClick={() => onStartCreate(key)}
                  >
                    Opprett internprosjekt
                  </Button>
                )}
              </div>
              {isCreating && (
                <CreateInternalProjectInlineForm
                  defaultName={p.name}
                  onCreated={(projectId) => {
                    onOverrideChange(key, { project_id: projectId });
                    onCancelCreate();
                  }}
                  onCancel={onCancelCreate}
                />
              )}
            </li>
          );
        })}
        {!showAll && items.length > 10 && (
          <li className="italic">
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="hover:underline"
            >
              +{items.length - 10} flere — vis alle
            </button>
          </li>
        )}
      </ul>
    </div>
  );
}

interface ItemTableProps {
  items: PreviewItem[];
  overrides: Record<string, ImportOverride>;
  onOverrideChange: (itemKey: string, patch: ImportOverride) => void;
  budgetLinesByProject: Map<
    string,
    Array<{ id: string; phase: string | null; description: string | null }>
  >;
  isAdmin: boolean;
  creatingFor: string | null;
  onStartCreate: (itemKey: string) => void;
  onCancelCreate: () => void;
}

function ItemTable({
  items,
  overrides,
  onOverrideChange,
  budgetLinesByProject,
  isAdmin,
  creatingFor,
  onStartCreate,
  onCancelCreate,
}: ItemTableProps) {
  return (
    <div className="overflow-x-auto border rounded-md">
      <table className="w-full text-xs">
        <thead className="bg-muted/30">
          <tr className="border-b text-[10px] uppercase tracking-wider text-muted-foreground">
            <th className="text-left py-2 px-2 font-medium">Item</th>
            <th className="text-left py-2 px-2 font-medium w-[80px]">Prosjekt</th>
            <th className="text-left py-2 px-2 font-medium w-[140px]">Fase</th>
            <th className="text-left py-2 px-2 font-medium">Oppgave</th>
            <th className="text-right py-2 px-2 font-medium w-[90px]">Timer</th>
            <th className="text-center py-2 px-2 font-medium w-[60px]">Skip</th>
            <th className="text-left py-2 px-2 font-medium w-[90px]">Match</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const key = String(item.monday_item_id);
            const override = overrides[key] ?? {};
            const effectivePhase = override.phase ?? item.parsed_phase ?? null;
            const effectiveBudgetLine =
              override.budget_line_id !== undefined
                ? override.budget_line_id
                : item.budget_line_id;
            // VIB-724a: override.project_id settes når admin oppretter
            // internprosjekt inline.
            const effectiveProjectId = override.project_id ?? item.project_id;
            const isSkipped = override.skip === true;
            const isCreating = creatingFor === key;
            const canCreateInternal =
              isAdmin && !effectiveProjectId && !isSkipped;

            const phaseLines = (budgetLinesByProject.get(effectiveProjectId ?? "") ?? []).filter(
              (b) => (effectivePhase ? b.phase === effectivePhase : true),
            );

            // Ny match-level basert på override (skip overstyrer alt).
            // Når project_id er satt via inline-opprett uten budget_line/phase,
            // havner vi i "project_only" — det er riktig fordi det fortsatt
            // mangler fase- og budgetlinje-info før neste dry-run.
            let level: PreviewItem["match_level"];
            if (isSkipped) level = "skipped";
            else if (effectiveBudgetLine) level = "exact";
            else if (effectivePhase) level = "phase";
            else if (effectiveProjectId) level = "project_only";
            else level = "skipped";

            const totalHours = item.total_seconds / 3600;

            return (
              <Fragment key={key}>
              <tr
                className={cn(
                  "border-b border-border/40 align-top",
                  isSkipped && "opacity-50",
                )}
              >
                <td className="py-2 px-2 max-w-[420px]">
                  <div
                    className="font-medium break-words leading-snug"
                    title={item.name}
                  >
                    {item.name}
                  </div>
                  {item.group_title && (
                    <div className="text-[10px] text-muted-foreground">
                      {item.group_title}
                    </div>
                  )}
                  {canCreateInternal && !isCreating && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-1 h-6 text-[10px] px-2"
                      onClick={() => onStartCreate(key)}
                    >
                      Opprett internprosjekt
                    </Button>
                  )}
                  {override.project_id && (
                    <div className="mt-1 text-[10px] text-green-700">
                      Knyttet til nytt internprosjekt
                    </div>
                  )}
                </td>
                <td className="py-2 px-2 tabular-nums">{item.project_code ?? "—"}</td>
                <td className="py-2 px-2">
                  <Select
                    value={effectivePhase ?? NONE_VALUE}
                    onValueChange={(v) =>
                      onOverrideChange(key, {
                        phase: v === NONE_VALUE ? null : v,
                        // Fjern budget_line-override når fasen endres,
                        // siden gammel valg ofte ikke lenger gyldig.
                        budget_line_id: null,
                      })
                    }
                    disabled={isSkipped}
                  >
                    <SelectTrigger className="h-7 text-xs px-2">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE_VALUE}>—</SelectItem>
                      {PHASE_OPTIONS.map((p) => (
                        <SelectItem key={p.value} value={p.value}>
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="py-2 px-2">
                  <Select
                    value={effectiveBudgetLine ?? NONE_VALUE}
                    onValueChange={(v) =>
                      onOverrideChange(key, {
                        budget_line_id: v === NONE_VALUE ? null : v,
                      })
                    }
                    disabled={isSkipped || phaseLines.length === 0}
                  >
                    <SelectTrigger className="h-7 text-xs px-2">
                      <SelectValue
                        placeholder={
                          phaseLines.length === 0
                            ? effectivePhase
                              ? "Ingen budget_lines i fasen"
                              : "Velg fase først"
                            : "—"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      <SelectItem value={NONE_VALUE}>— ufordelt</SelectItem>
                      {phaseLines.map((bl) => (
                        <SelectItem key={bl.id} value={bl.id}>
                          {bl.description ?? "(uten beskrivelse)"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="py-2 px-2 text-right tabular-nums">
                  {item.sessions_count > 0 ? (
                    <div className="leading-tight">
                      <div>{formatHours(totalHours)}</div>
                      <div className="text-[10px] text-muted-foreground">
                        {item.sessions_count} {item.sessions_count === 1 ? "økt" : "økter"}
                      </div>
                    </div>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
                <td className="py-2 px-2 text-center">
                  <input
                    type="checkbox"
                    aria-label={isSkipped ? "Inkluder igjen" : "Skip denne linjen"}
                    checked={isSkipped}
                    onChange={(e) =>
                      onOverrideChange(key, { skip: e.target.checked || undefined })
                    }
                    className="w-3.5 h-3.5 cursor-pointer accent-foreground/70"
                  />
                </td>
                <td className="py-2 px-2">
                  <span
                    className={cn(
                      "inline-block px-1.5 py-0.5 rounded text-[10px] font-medium",
                      MATCH_BADGE[level],
                    )}
                  >
                    {level}
                  </span>
                </td>
              </tr>
              {isCreating && (
                <tr className="border-b border-border/40">
                  <td colSpan={7} className="py-2 px-2 bg-muted/20">
                    <CreateInternalProjectInlineForm
                      defaultName={item.name}
                      onCreated={(projectId) => {
                        onOverrideChange(key, { project_id: projectId });
                        onCancelCreate();
                      }}
                      onCancel={onCancelCreate}
                    />
                  </td>
                </tr>
              )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
